import React, { useEffect, useState, useMemo, useCallback } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, UserPlus, Pencil, Trash2, KeyRound, Ban, CheckCircle, CreditCard, History as HistoryIcon, ShieldCheck, LogOut } from "lucide-react";

const ROLES = [
  { value: "free", label: "Free" },
  { value: "subscriber", label: "Subscriber" },
  { value: "dispatcher", label: "Dispatcher" },
  { value: "admin_dispatcher", label: "Admin Dispatcher" },
];

// US states + DC that the backend accepts as area_monitoring. Kept in sync with
// _VALID_STATES in /app/backend/routes/admin.py.
const US_STATES = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"],
  ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"],
  ["DC", "District of Columbia"], ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"],
  ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"], ["KS", "Kansas"],
  ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"], ["MD", "Maryland"],
  ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"], ["MS", "Mississippi"],
  ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"], ["NV", "Nevada"],
  ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"], ["NY", "New York"],
  ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"], ["OK", "Oklahoma"],
  ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"],
  ["SD", "South Dakota"], ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"],
  ["VT", "Vermont"], ["VA", "Virginia"], ["WA", "Washington"], ["WV", "West Virginia"],
  ["WI", "Wisconsin"], ["WY", "Wyoming"],
];

const CREATE_PLANS = [
  { value: "none", label: "Unpaid (no subscription)" },
  { value: "complimentary", label: "Complimentary (1 year, no charge)" },
  { value: "monthly", label: "Paid — Monthly" },
  { value: "quarterly", label: "Paid — Quarterly (3 mo)" },
  { value: "semi_annual", label: "Paid — Semi-Annual (6 mo)" },
  { value: "annual", label: "Paid — Annual (12 mo)" },
];

const PLANS = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly (3 mo)" },
  { value: "semi_annual", label: "Semi-Annual (6 mo)" },
  { value: "annual", label: "Annual (12 mo)" },
  { value: "complimentary", label: "Complimentary" },
  { value: "none", label: "Cancel / None" },
];

function roleBadge(role) {
  const color = {
    admin_dispatcher: "bg-red-500/20 text-red-400 border-red-500/40",
    dispatcher: "bg-blue-500/20 text-blue-400 border-blue-500/40",
    subscriber: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
    free: "bg-muted text-muted-foreground border-border",
    supervisor: "bg-amber-500/20 text-amber-400 border-amber-500/40",
  }[role] || "bg-muted";
  return <Badge variant="outline" className={`font-mono text-[10px] ${color}`}>{role}</Badge>;
}

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

function fmtDateShort(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(); } catch { return s; }
}

function daysFromNow(iso) {
  if (!iso) return null;
  try {
    const d = (new Date(iso) - Date.now()) / (1000 * 60 * 60 * 24);
    return Math.ceil(d);
  } catch { return null; }
}

export default function AdminPortalPage() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [subscribing, setSubscribing] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [auditRows, setAuditRows] = useState([]);
  const [tab, setTab] = useState("users");

  const loadUsers = useCallback(async () => {
    const r = await api.get("/admin/users");
    setUsers(r.data || []);
  }, []);
  const loadAudit = useCallback(async () => {
    const r = await api.get("/admin/audit-log", { params: { limit: 200 } });
    setAuditRows(r.data || []);
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { if (tab === "audit") loadAudit(); }, [tab, loadAudit]);

  const filtered = useMemo(() => {
    return users.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (q) {
        const t = q.toLowerCase();
        const hay = [u.email, u.username, u.name, u.area_monitoring].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(t)) return false;
      }
      return true;
    });
  }, [users, q, roleFilter]);

  const handleDelete = async (u) => {    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    await api.delete(`/admin/users/${u.id}`);
    toast.success("User deleted");
    loadUsers();
  };

  const handleKickSession = async (u) => {
    if (!window.confirm(`Force sign-out for ${u.email}?\n\nTheir next request will bounce them back to the login screen.`)) return;
    try {
      await api.post(`/admin/users/${u.id}/kick-session`);
      toast.success(`${u.username || u.email} kicked from active session`);
      loadUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kick failed");
    }
  };

  const handleToggleSuspend = async (u) => {
    await api.patch(`/admin/users/${u.id}`, { suspended: !u.suspended });
    toast.success(u.suspended ? "Account reactivated" : "Account suspended");
    loadUsers();
  };

  return (
    <div className="absolute inset-0 overflow-hidden flex flex-col" data-testid="admin-portal">
      <div className="border-b border-border bg-card/80 backdrop-blur px-4 py-3 shrink-0">
        <div className="font-mono text-xs text-primary tracking-widest">// ADMINISTRATION PORTAL</div>
        <h1 className="text-xl font-bold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" />User &amp; Subscription Management</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col overflow-hidden">
        <div className="px-4 pt-2 shrink-0">
          <TabsList>
            <TabsTrigger value="users" data-testid="admin-tab-users">Users ({users.length})</TabsTrigger>
            <TabsTrigger value="subs" data-testid="admin-tab-subs">Subscriptions</TabsTrigger>
            <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit Log</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="users" className="flex-1 overflow-auto p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="user-search" className="pl-10 font-mono h-9" placeholder="Search email / username / name…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-40 h-9" data-testid="role-filter"><SelectValue placeholder="Role" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={() => setCreateOpen(true)} data-testid="create-user-btn" className="h-9">
              <UserPlus className="w-4 h-4 mr-1.5" /> New User
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              <table className="w-full text-xs font-mono">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">USERNAME</th>
                    <th className="px-3 py-2 text-left">NAME</th>
                    <th className="px-3 py-2 text-left">EMAIL</th>
                    <th className="px-3 py-2 text-left">ROLE</th>
                    <th className="px-3 py-2 text-left">SUB</th>
                    <th className="px-3 py-2 text-left">EXPIRES</th>
                    <th className="px-3 py-2 text-left">LAST LOGIN</th>
                    <th className="px-3 py-2 text-left">SESSION</th>
                    <th className="px-3 py-2 text-left">STATUS</th>
                    <th className="px-3 py-2 text-right">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u) => {
                    const sub = u.subscription || {};
                    const daysLeft = daysFromNow(sub.paid_until);
                    const isPaid = !!(sub.plan && sub.plan !== "none" && (!sub.paid_until || (daysLeft != null && daysLeft >= 0)));
                    return (
                      <tr key={u.id} className="border-t border-border hover:bg-accent/30" data-testid={`user-row-${u.id}`}>
                        <td className="px-3 py-2 font-bold text-primary">{u.username || "—"}</td>
                        <td className="px-3 py-2">{u.name}</td>
                        <td className="px-3 py-2 text-muted-foreground">{u.email}</td>
                        <td className="px-3 py-2">{roleBadge(u.role)}</td>
                        <td className="px-3 py-2">
                          {isPaid ? (
                            <Badge variant="outline" className="border-emerald-500/50 text-emerald-400 font-mono text-[10px]" data-testid={`sub-badge-${u.id}`}>
                              PAID · {sub.plan}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-muted-foreground/40 text-muted-foreground font-mono text-[10px]" data-testid={`sub-badge-${u.id}`}>
                              UNPAID
                            </Badge>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {sub.paid_until ? (
                            <span className={daysLeft != null && daysLeft < 7 ? "text-amber-400" : ""}>
                              {fmtDateShort(sub.paid_until)}{daysLeft != null ? ` (${daysLeft}d)` : ""}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{fmtDateShort(u.last_login_at)}</td>
                        <td className="px-3 py-2">
                          {u.active_session_id ? (
                            <Badge variant="outline" className="border-emerald-500/50 text-emerald-400 text-[10px] font-mono" data-testid={`session-active-${u.id}`}>
                              ONLINE
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-[11px] font-mono">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {u.suspended
                            ? <Badge variant="outline" className="border-red-500/60 text-red-400 text-[10px]">SUSPENDED</Badge>
                            : <Badge variant="outline" className="border-green-500/40 text-green-400 text-[10px]">ACTIVE</Badge>}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7" title="Edit" data-testid={`edit-user-${u.id}`} onClick={() => setEditing(u)}><Pencil className="w-3.5 h-3.5" /></Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" title="Subscription" data-testid={`sub-user-${u.id}`} onClick={() => setSubscribing(u)}><CreditCard className="w-3.5 h-3.5" /></Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" title="Reset password" onClick={() => setResetting(u)}><KeyRound className="w-3.5 h-3.5" /></Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" title="Login history" onClick={() => setHistoryFor(u)}><HistoryIcon className="w-3.5 h-3.5" /></Button>
                            <Button
                              size="icon" variant="ghost"
                              className="h-7 w-7"
                              disabled={!u.active_session_id}
                              title={u.active_session_id ? "Force sign-out" : "No active session"}
                              data-testid={`kick-user-${u.id}`}
                              onClick={() => handleKickSession(u)}
                            >
                              <LogOut className={`w-3.5 h-3.5 ${u.active_session_id ? "text-amber-500" : "text-muted-foreground/40"}`} />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" title={u.suspended ? "Reactivate" : "Suspend"} onClick={() => handleToggleSuspend(u)}>
                              {u.suspended ? <CheckCircle className="w-3.5 h-3.5 text-green-500" /> : <Ban className="w-3.5 h-3.5 text-amber-500" />}
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" title="Delete" onClick={() => handleDelete(u)}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!filtered.length && (
                    <tr><td colSpan={10} className="px-4 py-8 text-center text-muted-foreground">No users match the filter.</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="subs" className="flex-1 overflow-auto p-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Subscription Roster</CardTitle>
              <CardDescription>All users with an active or expired subscription record.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-xs font-mono">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">USER</th>
                    <th className="px-3 py-2 text-left">PLAN</th>
                    <th className="px-3 py-2 text-left">STATUS</th>
                    <th className="px-3 py-2 text-left">START</th>
                    <th className="px-3 py-2 text-left">EXPIRES</th>
                    <th className="px-3 py-2 text-left">LAST PAYMENT</th>
                    <th className="px-3 py-2 text-left">AUTO-RENEW</th>
                    <th className="px-3 py-2 text-right">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {users.filter((u) => u.subscription && u.subscription.plan && u.subscription.plan !== "none").map((u) => {
                    const sub = u.subscription;
                    const lastPay = (u.payment_history || [])[((u.payment_history || []).length - 1)];
                    return (
                      <tr key={u.id} className="border-t border-border">
                        <td className="px-3 py-2">
                          <div className="font-bold text-primary">{u.username || "—"}</div>
                          <div className="text-muted-foreground">{u.email}</div>
                        </td>
                        <td className="px-3 py-2">{sub.plan}{sub.comp ? " ★" : ""}</td>
                        <td className="px-3 py-2">{sub.status}</td>
                        <td className="px-3 py-2">{fmtDateShort(sub.started_at)}</td>
                        <td className="px-3 py-2">{fmtDateShort(sub.paid_until)}</td>
                        <td className="px-3 py-2">{lastPay ? `$${(lastPay.amount || 0).toFixed(2)} · ${fmtDateShort(lastPay.timestamp)}` : "—"}</td>
                        <td className="px-3 py-2">{sub.auto_renew ? "Yes" : "No"}</td>
                        <td className="px-3 py-2 text-right">
                          <Button size="sm" variant="outline" className="h-7" onClick={() => setSubscribing(u)}>Manage</Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!users.some((u) => u.subscription && u.subscription.plan && u.subscription.plan !== "none") && (
                <div className="text-center text-muted-foreground py-8">No subscription records yet.</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit" className="flex-1 overflow-auto p-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Audit Trail</CardTitle>
              <CardDescription>Append-only log of incident + admin activity (last 200 events).</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-xs font-mono" data-testid="audit-table">
                <thead className="bg-muted/50 text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">TIMESTAMP</th>
                    <th className="px-3 py-2 text-left">ACTOR</th>
                    <th className="px-3 py-2 text-left">ACTION</th>
                    <th className="px-3 py-2 text-left">TARGET</th>
                    <th className="px-3 py-2 text-left">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {auditRows.map((a) => (
                    <tr key={a.id} className="border-t border-border">
                      <td className="px-3 py-2">{fmtDate(a.timestamp)}</td>
                      <td className="px-3 py-2"><span className="text-primary">{a.actor_username || "—"}</span> · {a.actor_name}</td>
                      <td className="px-3 py-2">{a.action}</td>
                      <td className="px-3 py-2 text-muted-foreground">{a.target_type}:{a.target_id?.slice(-8) || ""}</td>
                      <td className="px-3 py-2 text-muted-foreground">{a.ip || "—"}</td>
                    </tr>
                  ))}
                  {!auditRows.length && <tr><td colSpan={5} className="text-center text-muted-foreground py-6">No audit events yet.</td></tr>}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create */}
      <UserCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={loadUsers} />
      {/* Edit */}
      <UserEditDialog user={editing} onClose={() => setEditing(null)} onSaved={loadUsers} />
      {/* Reset */}
      <ResetPasswordDialog user={resetting} onClose={() => setResetting(null)} />
      {/* Subscription */}
      <SubscriptionDialog user={subscribing} onClose={() => setSubscribing(null)} onSaved={loadUsers} />
      {/* History */}
      <LoginHistoryDialog user={historyFor} onClose={() => setHistoryFor(null)} />
    </div>
  );
}

function UserCreateDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", area_monitoring: "NY",
    username_number: "", role: "dispatcher", telephone: "", address: "",
    subscription_plan: "none",
  });
  // Role-based username-number range: admin_dispatcher = 1–999, everyone else = 1000–9999
  const isAdminRole = form.role === "admin_dispatcher" || form.role === "admin";
  const numRange = isAdminRole ? [1, 999] : [1000, 9999];
  const submit = async () => {
    const num = String(form.username_number || "").trim();
    const body = { ...form };
    if (num) {
      const n = parseInt(num, 10);
      if (!Number.isFinite(n) || n < numRange[0] || n > numRange[1]) {
        toast.error(`Username # must be ${numRange[0]}–${numRange[1]} for ${form.role} (or leave blank for auto)`);
        return;
      }
      body.username_number = n;
    } else {
      delete body.username_number;
    }
    try {
      const r = await api.post("/admin/users", body);
      const preview = r?.data?.username ? ` — ${r.data.username}` : "";
      toast.success(`User created${preview} — default password: psa2025`);
      onClose();
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };
  const previewUsername = form.username_number
    ? `${form.username_number}${form.area_monitoring}`
    : `auto${form.area_monitoring} (next free ${numRange[0]}–${numRange[1]})`;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle>Create User</DialogTitle>
          <DialogDescription>
            Default password is <code>psa2025</code> — user will change it on first login.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="create-first-name" />
            <Input placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          </div>
          <Input placeholder="email@…" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="create-email" />
          <Input placeholder="Address (optional)" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          <Input placeholder="Telephone (optional)" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} />

          <div className="pt-1">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Username</div>
            <div className="grid grid-cols-[1fr_140px] gap-2">
              <Select value={form.area_monitoring} onValueChange={(v) => setForm({ ...form, area_monitoring: v })}>
                <SelectTrigger data-testid="create-state-select"><SelectValue placeholder="Area / State" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {US_STATES.map(([abbr, name]) => (
                    <SelectItem key={abbr} value={abbr}>{abbr} — {name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder={`# ${numRange[0]}–${numRange[1]} (auto)`}
                type="number"
                min={numRange[0]}
                max={numRange[1]}
                value={form.username_number}
                onChange={(e) => setForm({ ...form, username_number: e.target.value })}
                data-testid="create-username-number"
              />
            </div>
            <div className="text-[10px] font-mono text-muted-foreground mt-1" data-testid="create-username-preview">
              Preview: <span className="text-primary">{previewUsername}</span>
            </div>
          </div>

          <div className="pt-1 grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Account Type</div>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="create-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Subscription</div>
              <Select value={form.subscription_plan} onValueChange={(v) => setForm({ ...form, subscription_plan: v })}>
                <SelectTrigger data-testid="create-sub-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CREATE_PLANS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} data-testid="create-submit">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserEditDialog({ user, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  useEffect(() => { setForm(user ? { ...user } : null); }, [user]);
  if (!user) return null;
  const submit = async () => {
    try {
      await api.patch(`/admin/users/${user.id}`, {
        first_name: form.first_name, last_name: form.last_name,
        address: form.address, telephone: form.telephone,
        area_monitoring: form.area_monitoring, role: form.role,
      });
      toast.success("User updated");
      onClose(); onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };
  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="edit-user-dialog">
        <DialogHeader><DialogTitle>Edit User · {user.username}</DialogTitle></DialogHeader>
        {form && (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="First name" value={form.first_name || ""} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              <Input placeholder="Last name" value={form.last_name || ""} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            </div>
            <Input placeholder="Address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            <Input placeholder="Telephone" value={form.telephone || ""} onChange={(e) => setForm({ ...form, telephone: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Area / State</div>
                <Select value={form.area_monitoring || ""} onValueChange={(v) => setForm({ ...form, area_monitoring: v })}>
                  <SelectTrigger data-testid="edit-state-select"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {US_STATES.map(([abbr, name]) => (
                      <SelectItem key={abbr} value={abbr}>{abbr} — {name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Account Type</div>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="edit-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, onClose }) {
  const [pwd, setPwd] = useState("");
  useEffect(() => { setPwd(""); }, [user]);
  if (!user) return null;
  const submit = async (useDefault) => {
    try {
      const body = useDefault ? { force_change: true } : { new_password: pwd, force_change: true };
      const r = await api.post(`/admin/users/${user.id}/reset-password`, body);
      toast.success(r.data.default_assigned ? "Reset to default (psa2025)" : "Password set");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reset failed"); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Reset Password · {user.username}</DialogTitle><DialogDescription>User will be forced to change on next login.</DialogDescription></DialogHeader>
        <Input placeholder="Leave blank to use default (psa2025)" type="text" value={pwd} onChange={(e) => setPwd(e.target.value)} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="outline" onClick={() => submit(true)}>Use Default</Button>
          <Button onClick={() => submit(false)} disabled={!pwd}>Set Password</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SubscriptionDialog({ user, onClose, onSaved }) {
  const [plan, setPlan] = useState("monthly");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => { if (user) { setPlan(user.subscription?.plan || "monthly"); setAmount(""); setNote(""); } }, [user]);
  if (!user) return null;
  const submit = async () => {
    try {
      await api.post(`/admin/users/${user.id}/subscription`, {
        plan, payment_amount: amount ? parseFloat(amount) : undefined, payment_method: "manual", note,
      });
      toast.success(plan === "none" ? "Subscription cancelled" : "Subscription saved");
      onClose(); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="subscription-dialog">
        <DialogHeader>
          <DialogTitle>Subscription · {user.username}</DialogTitle>
          <DialogDescription>
            Current: {user.subscription?.plan || "none"} · expires {fmtDateShort(user.subscription?.paid_until)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Select value={plan} onValueChange={setPlan}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{PLANS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
          <Input placeholder="Payment amount (optional)" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <Input placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          {!!(user.payment_history || []).length && (
            <div className="border-t border-border pt-2">
              <div className="text-xs font-mono text-muted-foreground mb-1">PAYMENT HISTORY</div>
              <div className="max-h-32 overflow-y-auto space-y-1 text-xs">
                {(user.payment_history || []).slice().reverse().map((p) => (
                  <div key={p.id} className="flex justify-between gap-2">
                    <span>{fmtDate(p.timestamp)}</span>
                    <span>${(p.amount || 0).toFixed(2)} · {p.plan}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} data-testid="sub-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LoginHistoryDialog({ user, onClose }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!user) return;
    api.get(`/admin/users/${user.id}/login-history`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [user]);
  if (!user) return null;
  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Login History · {user.username}</DialogTitle></DialogHeader>
        <div className="max-h-72 overflow-y-auto text-xs font-mono">
          {rows.length === 0 && <div className="text-center text-muted-foreground py-4">No logins recorded.</div>}
          {rows.map((r) => (
            <div key={r.id} className="flex justify-between gap-2 border-b border-border py-1">
              <span>{fmtDate(r.timestamp)}</span>
              <span className="text-muted-foreground">{r.ip || "—"}</span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
